<template>
    <div>
        <DocumentInformationSection
            v-model="draft.documentInformation"
        />

        <ClassificationSection
            v-model="draft.classification"
        />

        <LegislationsSection
            v-model="draft.legislations"
        />

        <ProductsSection
            v-if="canEditExtractedProducts"
            v-model="draft.products"
        />

        <ProductLinksSection
            v-if="canEditProductLinks"
            v-model="draft.productLinks"
            :supplier-id="supplierId"
            :products="draft.products"
        />

        <SubstancesSection
            v-if="canEditExtractedSubstances"
            v-model="draft.substanceAssessment.substances"
            :links="draft.substanceLinks"
            @update:links="draft.substanceLinks = $event"
        />

        <ExemptionsSection
            v-model="draft.exemptions"
        />
    </div>
</template>

<script>
import DocumentInformationSection from './sections/DocumentInformationSection.vue'
import ClassificationSection from './sections/ClassificationSection.vue'
import LegislationsSection from './sections/LegislationsSection.vue'
import ProductsSection from './sections/ProductsSection.vue'
import SubstancesSection from './sections/SubstancesSection.vue'
import ProductLinksSection from './sections/ProductLinksSection.vue'
import ExemptionsSection from './sections/ExemptionsSection.vue'

function deepClone(value) {
    return JSON.parse(JSON.stringify(value || {}))
}

function isEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b)
}

function normalizeDraft(payload) {
    const draft = deepClone(payload)

    if (!draft.documentInformation || typeof draft.documentInformation !== 'object') {
        draft.documentInformation = {}
    }

    if (!draft.classification || typeof draft.classification !== 'object') {
        draft.classification = {}
    }

    if (!Array.isArray(draft.legislations)) {
        draft.legislations = []
    }

    if (!Array.isArray(draft.products)) {
        draft.products = []
    }
    draft.products = draft.products.map(item => {
        const row = item && typeof item === 'object' ? deepClone(item) : {}

        delete row.id
        delete row.productId

        if (row.extractedProductId === undefined) {
            row.extractedProductId = null
        }

        return row
    })

    if (!Array.isArray(draft.exemptions)) {
        draft.exemptions = []
    }

    if (!draft.substanceAssessment || typeof draft.substanceAssessment !== 'object') {
        draft.substanceAssessment = {}
    }

    if (!Array.isArray(draft.substanceAssessment.substances)) {
        draft.substanceAssessment.substances = []
    }
    draft.substanceAssessment.substances = draft.substanceAssessment.substances.map(item => {
        const row = item && typeof item === 'object' ? deepClone(item) : {}

        delete row.id
        delete row.matchedSubstanceId
        delete row.substanceId

        if (row.extractedSubstanceId === undefined) {
            row.extractedSubstanceId = null
        }

        if (!row.concentration || typeof row.concentration !== 'object') {
            row.concentration = {
                unit: 'w/w',
                basis: 'homogeneous_material',
                value: null,
                operator: '<=',
            }
        }

        return row
    })

    if (!Array.isArray(draft.productLinks)) {
        draft.productLinks = []
    }
    draft.productLinks = draft.productLinks.map(item => {
        const row = item && typeof item === 'object' ? deepClone(item) : {}

        return {
            extractedProductId: row.extractedProductId !== undefined ? row.extractedProductId : null,
            productId: row.productId !== undefined && row.productId !== null ? String(row.productId) : '',
            type: row.type || 'manual',
            status: row.status || 'accepted',
            confidence: row.confidence !== undefined ? row.confidence : null,
            reason: row.reason !== undefined ? row.reason : '',
        }
    })

    if (!Array.isArray(draft.substanceLinks)) {
        draft.substanceLinks = []
    }
    draft.substanceLinks = draft.substanceLinks.map(item => {
        const row = item && typeof item === 'object' ? deepClone(item) : {}

        return {
            extractedSubstanceId: row.extractedSubstanceId !== undefined ? row.extractedSubstanceId : null,
            substanceId: row.substanceId !== undefined && row.substanceId !== null ? row.substanceId : '',
            type: row.type || 'manual',
            status: row.status || 'accepted',
            confidence: row.confidence !== undefined ? row.confidence : null,
            reason: row.reason !== undefined ? row.reason : '',
        }
    })

    return draft
}

export default {
    name: 'DocumentComplianceEditor',
    components: {
        DocumentInformationSection,
        ClassificationSection,
        LegislationsSection,
        ProductsSection,
        SubstancesSection,
        ProductLinksSection,
        ExemptionsSection,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
        supplierId: {
            type: [String, Number],
            default: null,
        },
        reviewCapabilities: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            draft: normalizeDraft(this.value),
        }
    },
    computed: {
        canEditExtractedProducts() {
            return this.reviewCapabilities.canEditExtractedProducts !== false
        },
        canEditExtractedSubstances() {
            return this.reviewCapabilities.canEditExtractedSubstances !== false
        },
        canEditProductLinks() {
            return this.reviewCapabilities.canEditProductLinks !== false
        },
        canEditSubstanceLinks() {
            return this.reviewCapabilities.canEditSubstanceLinks !== false
        },
        validExtractedProductIds() {
            return this.draft.products
                .map(item => (item && item.extractedProductId !== undefined && item.extractedProductId !== null
                    ? String(item.extractedProductId)
                    : null))
                .filter(Boolean)
        },
    },
    watch: {
        value: {
            deep: true,
            handler(newValue) {
                const normalized = normalizeDraft(newValue)
                if (isEqual(normalized, this.draft)) {
                    return
                }

                this.draft = normalized
            },
        },
        draft: {
            deep: true,
            handler(newValue) {
                const emitted = deepClone(newValue)
                const current = normalizeDraft(this.value)

                if (isEqual(emitted, current)) {
                    return
                }

                this.$emit('input', emitted)
            },
        },
        'draft.products': {
            deep: true,
            handler() {
                const validIds = this.validExtractedProductIds

                this.draft.productLinks = this.draft.productLinks.map(item => {
                    if (!item || item.extractedProductId === undefined || item.extractedProductId === null) {
                        return item
                    }

                    if (validIds.includes(String(item.extractedProductId))) {
                        return item
                    }

                    return {
                        ...item,
                        extractedProductId: null,
                    }
                })
            },
        },
    },
}
</script>
